import { NODE_TYPES, MODEL_TYPES } from './constants';

// Generate unique ID
export const generateId = () => {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Check if node matches diagramFilter settings
export const isNodeMatchingFilter = (node, filter) => {
  if (!filter || !filter.active) return true;

  // 1. Filter by Entity Type
  if (filter.entityTypes && filter.entityTypes.length > 0) {
    if (!filter.entityTypes.includes(node.type)) {
      return false;
    }
  }

  // 2. Filter by Text Search (Name, Schema, Description, Fields)
  if (filter.searchQuery && filter.searchQuery.trim()) {
    const q = filter.searchQuery.trim().toLowerCase();
    const nameMatch = node.data?.name?.toLowerCase().includes(q);
    const schemaMatch = node.data?.schema?.toLowerCase().includes(q);
    const descMatch = node.data?.description?.toLowerCase().includes(q);
    const fieldMatch = (node.data?.fields || []).some(f =>
      f.name?.toLowerCase().includes(q) || f.type?.toLowerCase().includes(q)
    );
    if (!nameMatch && !schemaMatch && !descMatch && !fieldMatch) {
      return false;
    }
  }

  // 3. Filter by Tags
  if (filter.tags && filter.tags.length > 0) {
    const nodeTags = (node.data?.tags || []).map(t => (typeof t === 'string' ? t : t.text || t.name).toLowerCase());
    const matchesAnyTag = filter.tags.some(tag => nodeTags.includes(tag.toLowerCase()));
    if (!matchesAnyTag) {
      return false;
    }
  }

  // 4. Filter by Domain
  if (filter.domain && filter.domain.trim()) {
    const dTarget = filter.domain.trim().toLowerCase();
    const nodeDomains = (node.data?.domains || []).map(d => (typeof d === 'string' ? d : d.text || d.name).toLowerCase());
    const matchesDomain = nodeDomains.some(dom => dom.includes(dTarget));
    if (!matchesDomain) {
      return false;
    }
  }

  // 5. Filter by Schema
  if (filter.schema && filter.schema.trim()) {
    const sTarget = filter.schema.trim().toLowerCase();
    const nodeSchema = (node.data?.schema || '').toLowerCase();
    if (!nodeSchema.includes(sTarget)) {
      return false;
    }
  }

  return true;
};

// Get node type based on model type
export const getNodeTypeForModel = (modelType) => {
  switch (modelType) {
    case MODEL_TYPES.RELATIONAL:
      return NODE_TYPES.TABLE;
    case MODEL_TYPES.NOSQL:
      return NODE_TYPES.COLLECTION;
    case MODEL_TYPES.ANALYTICAL:
      return NODE_TYPES.FACT;
    default:
      return NODE_TYPES.TABLE;
  }
};

// Check if node type supports views
export const supportsViews = (modelType) => {
  return modelType === MODEL_TYPES.RELATIONAL || modelType === MODEL_TYPES.ANALYTICAL;
};

// Get default field structure
export const createDefaultField = (name = '', type = 'VARCHAR', isPrimaryKey = false) => ({
  name,
  type,
  nullable: isPrimaryKey ? false : true, // PK is automatically NOT NULL
  constraints: [],
  isPrimaryKey,
  isForeignKey: false,
  description: '',
  checkConstraint: ''
});

// Generate unique entity name based on existing entities
export const generateEntityName = (existingNodes) => {
  // Get all entity names that match the "New Entity" pattern
  const newEntityNames = existingNodes
    .map(node => node.data?.name)
    .filter(name => name && (name === 'New Entity' || name.match(/^New Entity \d+$/)));
  
  if (newEntityNames.length === 0) {
    return 'New Entity';
  }
  
  // Extract numbers from existing names
  const numbers = newEntityNames
    .map(name => {
      if (name === 'New Entity') return 1;
      const match = name.match(/^New Entity (\d+)$/);
      return match ? parseInt(match[1], 10) : 0;
    })
    .filter(num => num > 0);
  
  // Find the next available number
  const maxNumber = numbers.length > 0 ? Math.max(...numbers) : 0;
  const nextNumber = maxNumber + 1;
  
  return nextNumber === 1 ? 'New Entity' : `New Entity ${nextNumber}`;
};

// Create new node
export const createNode = (type, position, name = 'New Entity', existingNodes = []) => {
  // Generate unique name if using default
  const entityName = name === 'New Entity' ? generateEntityName(existingNodes) : name;
  
  // Text nodes have different structure
  if (type === NODE_TYPES.TEXT) {
    return {
      id: generateId(),
      type,
      position,
      data: {
        name: entityName === 'New Entity' || entityName.startsWith('New Entity ') ? 'Note' : entityName,
        content: '**Double-click to edit**\n\nAdd notes, documentation, or comments using Markdown!',
        backgroundColor: '#FEF9C3',
        width: 300
      }
    };
  }
  
  // Regular entity nodes
  // For TABLE, FACT, and DIMENSION types, create 'id' field as Primary Key by default
  const shouldBeDefaultPK = type === NODE_TYPES.TABLE || 
                            type === NODE_TYPES.FACT || 
                            type === NODE_TYPES.DIMENSION;
  
  return {
    id: generateId(),
    type,
    position,
    data: {
      name: entityName,
      schema: '',
      fields: [createDefaultField('id', 'INTEGER', shouldBeDefaultPK)],
      color: null,
      description: '',
      tags: [],   // Initialize empty tags array
      domains: [] // Initialize empty domains array
    }
  };
};

// Create new edge
export const createEdge = (source, target, type = 'default', label = '') => ({
  id: generateId(),
  source,
  target,
  type,
  data: {
    label,
    cardinality: '1:N',
    sourceField: '',
    targetField: ''
  }
});

// Download JSON file
export const downloadJSON = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Parse JSON file
export const parseJSONFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target.result);
        resolve(json);
      } catch (error) {
        reject(new Error('Invalid JSON file'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

// Validate model data
export const validateModelData = (data) => {
  const errors = [];
  
  if (!data.name || data.name.trim() === '') {
    errors.push('Model name is required');
  }
  
  if (!data.type || !Object.values(MODEL_TYPES).includes(data.type)) {
    errors.push('Invalid model type');
  }
  
  if (!Array.isArray(data.nodes)) {
    errors.push('Nodes must be an array');
  }
  
  if (!Array.isArray(data.edges)) {
    errors.push('Edges must be an array');
  }
  
  return errors;
};

// Format date
export const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleString('pt-BR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Truncate text
export const truncate = (text, length = 50) => {
  if (!text) return '';
  return text.length > length ? `${text.substring(0, length)}...` : text;
};

// Debounce function
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// LocalStorage helpers
export const storage = {
  get: (key, defaultValue = null) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch {
      return defaultValue;
    }
  },
  
  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch {
      return false;
    }
  },
  
  remove: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch {
      return false;
    }
  }
};
